<template>
	<AppLayout
		:title="trans('admin.users.boards.certifications.create.title', {years: board.years})"
		:return-url="route('association.boards.certifications.index')"
		:return-label="trans('admin.users.boards.certifications.title')"
	>
		<Modal :id="modalId">
			<template #title>{{ trans('admin.certifications.confirm') }}</template>
			<Alert :color="Color.Amber">{{ trans('admin.certifications.warning') }}</Alert>
			<LabeledObject>
				<template #label>{{ trans('account.types.board_member') }}</template>
				<p v-if="certification.board_members.length > 0">{{ certification.board_members.map((m) => m.user.full_name).join(', ') }}.</p>
				<p v-else>{{ trans('general.n_a') }}.</p>
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('account.types.workgroup_member') }}</template>
				<p v-if="certification.workgroup_members.length > 0">{{ certification.workgroup_members.map((m) => m.user.full_name).join(', ') }}.</p>
				<p v-else>{{ trans('general.n_a') }}.</p>
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="create">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton v-if="certification.certificate_id > 0 && (certification.board_members.length > 0 || certification.workgroup_members.length > 0)" :color="Color.Green" @click.stop="showConfirmModal">
				{{ trans('general.continue') }}
				<i class="fa-solid fa-arrow-right ml-2"/>
			</PrimaryButton>
		</template>
		<LabeledObject class="place-self-start" required>
			<template #label>{{ trans('admin.certifications.certificate') }}</template>
			<select id="select-certificate" v-model="certification.certificate_id">
				<option :value="0">{{ trans('general.please_select') }}</option>
				<option v-for="certificate in certificates" :key="`certificate-${certificate.id}`" :value="certificate.id">{{ certificate.title }}</option>
			</select>
		</LabeledObject>
		<p class="font-bold">{{ trans('account.types.board_member') }}</p>
		<CardGrid>
			<Card v-for="eligibleMember in eligibleMembers" :key="`eligible-member-${eligibleMember.id}`" :title="eligibleMember.user.full_name" :subtitle="eligibleMember.legal_rank ? trans(`association.boards.legal_ranks.${eligibleMember.legal_rank}`) : trans('general.n_a')" class="cursor-pointer" @click.stop="toggleMember(eligibleMember)">
				<i v-if="certification.board_members.map((m: BoardMember) => m.id).includes(eligibleMember.id)" class="fa-solid fa-toggle-on text-green-600"/>
				<i v-else class="fa-solid fa-toggle-off text-red-600"/>
			</Card>
		</CardGrid>
		<p class="font-bold">{{ trans('account.types.workgroup_member') }}</p>
		<CardGrid>
			<Card v-for="eligibleMember in eligibleWorkgroupMembers" :key="`eligible-workgroup-member-${eligibleMember.id}`" :title="eligibleMember.user.full_name" :subtitle="eligibleMember.area.name" class="cursor-pointer" @click.stop="toggleWorkgroupMember(eligibleMember)">
				<i v-if="certification.workgroup_members.map((m: BoardWorkgroupMember) => m.id).includes(eligibleMember.id)" class="fa-solid fa-toggle-on text-green-600"/>
				<i v-else class="fa-solid fa-toggle-off text-red-600"/>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Alert from '@/Components/UI/Alerts/Alert.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Board} from '@/Types/Association/Boards/Board'
import {type Certificate} from '@/Types/Certificates/Certificate'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import type {BoardMember} from '@/Types/Association/Boards/BoardMember'
import type {BoardWorkgroupMember} from '@/Types/Association/Boards/BoardWorkgroupMember'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

interface CertificationRepresentation {
	certifiable_type:string,
	certifiable_id:number,
	certificate_id:number,
	board_members:Array<BoardMember>,
	workgroup_members:Array<BoardWorkgroupMember>,
}

const props = defineProps<{
	board:Board,
	certificates:Array<Certificate>,
	eligibleMembers:Array<BoardMember>,
	eligibleWorkgroupMembers:Array<BoardWorkgroupMember>,
}>()

const board:Ref<Board> = ref(props.board)
const working:Ref<boolean> = ref(false)
const certificates:Ref<Array<Certificate>> = ref(props.certificates)
const eligibleMembers:Ref<Array<BoardMember>> = ref(props.eligibleMembers)
const modalId:Ref<string> = ref('confirm-certifications-create-modal')
const eligibleWorkgroupMembers:Ref<Array<BoardWorkgroupMember>> = ref(props.eligibleWorkgroupMembers)
const certification:Ref<CertificationRepresentation> = ref({
	certifiable_type: 'board',
	certifiable_id: board.value.id,
	certificate_id: 0,
	board_members: [],
	workgroup_members: [],
})

function showConfirmModal():void{
	showModal(modalId.value)
}

function toggleMember(member:BoardMember):void{
	const index = certification.value.board_members.findIndex((m: BoardMember) => m.id === member.id)
	if(index === -1){
		certification.value.board_members.push(member)
	} else {
		certification.value.board_members.splice(index, 1)
	}
}

function toggleWorkgroupMember(member:BoardWorkgroupMember):void{
	const index = certification.value.workgroup_members.findIndex((m: BoardWorkgroupMember) => m.id === member.id)
	if(index === -1){
		certification.value.workgroup_members.push(member)
	} else {
		certification.value.workgroup_members.splice(index, 1)
	}
}

function create():void{
	if(!working.value){
		closeModal(modalId.value)
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.boards.certifications.store', board.value.id), certification.value).then(() => {
			certification.value.board_members = []
			certification.value.certificate_id = 0
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>